import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link, useLocation } from 'react-router-dom';
import { auth, googleProvider, signInWithPopup, createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail, handleGoogleLogin } from '../firebase';
import { YachtLogo } from '../components/YachtLogo';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Lock, ArrowRight, Github, Sparkles, AlertCircle, Loader2 } from 'lucide-react';
import { toast, Toaster } from 'sonner';

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isSignUp = searchParams.get('mode') === 'signup' || location.pathname === '/signup';
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      if (isSignUp) {
        await createUserWithEmailAndPassword(auth, email, password);
        toast.success("Account created successfully!");
      } else {
        await signInWithEmailAndPassword(auth, email, password);
        toast.success("Logged in successfully!");
      }
      navigate('/studio');
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Authentication failed");
      toast.error(err.message || "Authentication failed");
    } finally {
      setIsLoading(false);
    }
  };

  const onGoogleLogin = async () => {
    try {
      await handleGoogleLogin();
      navigate('/studio');
    } catch (err: any) {
      toast.error("Google login failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center justify-center p-6 relative overflow-hidden">
      <Toaster position="top-right" theme="dark" />
      
      {/* Background Decor */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-[#0074D9]/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-[#0074D9]/5 rounded-full blur-[120px]" />
      </div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="flex flex-col items-center mb-12">
          <Link to="/" className="flex items-center gap-3 mb-8">
            <YachtLogo className="w-8 h-8" />
            <span className="font-mono text-2xl tracking-tighter uppercase">Yacht Labs</span>
          </Link>
          <h1 className="text-4xl font-sans font-bold tracking-tight text-center mb-2">
            {isSignUp ? "Build the future." : "Welcome back."}
          </h1>
          <p className="text-zinc-500 font-mono text-[10px] uppercase tracking-[.2em]">
            {isSignUp ? "Mission Control Acquisition" : "Authentication Required"}
          </p>
        </div>

        <div className="bg-[#0A0A0A] border border-white/5 rounded-3xl p-8 shadow-2xl backdrop-blur-xl">
          <form onSubmit={handleEmailAuth} className="space-y-6">
            <div className="space-y-2">
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 ml-1">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600" size={16} />
                <input 
                  type="email" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-black border border-white/5 rounded-xl py-4 pl-12 pr-6 text-sm focus:outline-none focus:border-[#0074D9] focus:ring-1 focus:ring-[#0074D9] transition-all"
                  placeholder="name@company.com"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center ml-1">
                <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Password</label>
                {!isSignUp && (
                  <button 
                    type="button"
                    onClick={() => {
                        if(!email) { toast.error("Enter your email first"); return; }
                        sendPasswordResetEmail(auth, email).then(() => toast.success("Reset link sent!"));
                    }}
                    className="text-[10px] font-mono uppercase tracking-widest text-[#0074D9] hover:underline"
                  >
                    Forgot?
                  </button>
                )}
              </div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600" size={16} />
                <input 
                  type="password" 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-black border border-white/5 rounded-xl py-4 pl-12 pr-6 text-sm focus:outline-none focus:border-[#0074D9] focus:ring-1 focus:ring-[#0074D9] transition-all"
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-500 text-xs animate-shake">
                <AlertCircle size={14} />
                <span>{error}</span>
              </div>
            )}

            <button 
              type="submit"
              disabled={isLoading}
              className="w-full py-4 bg-white text-black font-mono uppercase tracking-widest text-xs font-bold rounded-xl hover:bg-zinc-200 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isLoading ? <Loader2 className="animate-spin" size={16} /> : (
                <>
                  {isSignUp ? "Initialize Account" : "Access Studio"}
                  <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>

          <div className="mt-8 relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/5" />
            </div>
            <div className="relative flex justify-center text-[10px] uppercase font-mono tracking-widest">
              <span className="bg-[#0A0A0A] px-4 text-zinc-600">Or continue with</span>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-4">
            <button 
              onClick={onGoogleLogin}
              className="flex items-center justify-center gap-3 py-3 bg-black border border-white/5 rounded-xl hover:bg-zinc-900 transition-all text-xs font-mono uppercase tracking-widest"
            >
              <img src="https://svgl.app/library/google.svg" alt="Google" className="h-4 w-auto" />
              Google
            </button>
            <button 
              className="flex items-center justify-center gap-3 py-3 bg-black border border-white/5 rounded-xl hover:bg-zinc-900 transition-all text-xs font-mono uppercase tracking-widest opacity-50 cursor-not-allowed"
              disabled
            >
              <Github size={16} />
              GitHub
            </button>
          </div>
        </div>

        <p className="mt-8 text-center text-zinc-500 text-[10px] font-mono uppercase tracking-widest">
          {isSignUp ? "Already have an account?" : "Don't have an account?"} {' '}
          <Link to={isSignUp ? "/login" : "/signup"} className="text-white hover:underline">
            {isSignUp ? "Log In" : "Sign Up Free"}
          </Link>
        </p>
      </motion.div>

      {/* Footer Meta */}
      <div className="mt-20 flex items-center gap-8 text-[9px] font-mono uppercase tracking-widest text-zinc-700">
        <Link to="/terms" className="hover:text-zinc-400">Terms of Service</Link>
        <Link to="/privacy" className="hover:text-zinc-400">Privacy Policy</Link>
        <span className="flex items-center gap-2">
          <Sparkles size={10} />
          AI Powered Studio
        </span>
      </div>
    </div>
  );
}
